import { LoaderFunction } from 'react-router-dom';

// Sentry
import * as Sentry from '@sentry/react';

// GQL
import { client } from '../../../../gql/client';
import { GET_SURVEILLANCE_ROUTE_PRESETS } from '../../../../gql/queries/surveillanceRoutePresets';
import { GET_ROADS } from '../../../../gql/queries/geoPositions/roads';

// Utils
import { AuthContextType } from '../../../../hooks/useAuth';
import { formatRoadWithRange } from '../../../../utils/surveillanceRoute';

// Types
import { SurveillanceRoutePreset } from '../../../../types/surveillanceRoute';
import { RoutePresetRow } from '../../../../components/display/tables/formTables/managementTables/RoutePresetsManagementTable/types';
import { RoadBounds } from '../../../../components/modals/submodals/RoutePresetModal/types';
import { RoutePresetsManagementLoaderData } from './types';

/**
 * Los rangos de la plantilla en el orden de sus carreteras.
 *
 * La plantilla SIEMPRE declara un rango por carretera (a diferencia del
 * recorrido, que con una sola cae a sus escalares), así que aquí no hace falta
 * la cascada de `resolveRouteRoadRanges`. Una carretera sin rango se omite en
 * vez de inventarle uno.
 */
const rangesInRoadOrder = (preset: SurveillanceRoutePreset) =>
  preset.roadIDs.flatMap((roadId) => {
    const range = preset.roadRanges.find((r) => r.roadId === roadId);
    return range ? [range] : [];
  });

const load = async (
  userData: AuthContextType | null
): Promise<RoutePresetsManagementLoaderData> => {
  const vacio: RoutePresetsManagementLoaderData = {
    activePresets: [],
    inactivePresets: [],
    roads: [],
    presetsById: {},
  };

  try {
    if (!userData?.user) return vacio;

    const [presetsResponse, roadsResponse] = await Promise.all([
      client.query({
        query: GET_SURVEILLANCE_ROUTE_PRESETS,
        // network-only: esta pantalla ES la fuente de verdad del catálogo, no
        // puede servir una copia de caché tras crear, editar o desactivar.
        variables: { onlyActive: false },
        fetchPolicy: 'network-only',
      }),
      client.query({
        // El catálogo de vías alimenta el desplegable del modal y los extremos
        // read-only de cada fila. cache-first: son datos maestros.
        query: GET_ROADS,
        fetchPolicy: 'cache-first',
      }),
    ]);

    const presets: SurveillanceRoutePreset[] =
      presetsResponse.data?.surveillanceRoutePresets ?? [];
    const roads: RoadBounds[] = (roadsResponse.data?.roads ?? []).map(
      (road: RoadBounds) => ({
        id: road.id,
        name: road.name,
        pkInitKm: road.pkInitKm,
        pkInitMeter: road.pkInitMeter,
        pkEndKm: road.pkEndKm,
        pkEndMeter: road.pkEndMeter,
      })
    );
    const roadNameById = new Map(roads.map((road) => [road.id, road.name]));

    /**
     * 🔴 Una línea por carretera, con su rango entre paréntesis — el formato de
     * la vista previa del recorrido. El rango va SIEMPRE pegado a su vía: nunca
     * un par suelto, y nunca fundido con un min/max, porque los PK de una
     * troncal y su ramal no son comparables.
     *
     * A diferencia de `formatRoadsWithRanges` (la cabecera del detalle), aquí el
     * rango se enseña **también con una sola carretera**: allí lo dicen las
     * filas «PK Inicio»/«PK Fin» de al lado, y en esta tabla no hay tales filas.
     */
    const toRow = (preset: SurveillanceRoutePreset): RoutePresetRow => {
      const rangeByRoadId = new Map(
        rangesInRoadOrder(preset).map((r) => [r.roadId, r])
      );
      return {
        id: preset.id,
        name: preset.name,
        isActive: preset.isActive,
        roadsLabel: preset.roadIDs
          .map((roadId) =>
            // Nombres, no ids: la columna la lee una persona. Una vía que no
            // esté en el catálogo cae a su id — omitirla diría que la plantilla
            // tiene menos carreteras de las que tiene, que es el fallo a evitar.
            //
            // Formato compartido con la fila «Carretera» del recorrido. Lo que
            // NO se comparte es el pegamento: allí van por comas y con una
            // sola vía se calla el rango, porque la ficha ya lo enseña al
            // lado; aquí una línea por vía y el rango SIEMPRE, que es lo único
            // que distingue la plantilla de ida de la de vuelta.
            formatRoadWithRange(
              roadNameById.get(roadId) ?? roadId,
              rangeByRoadId.get(roadId)
            )
          )
          .join('\n'),
      };
    };

    return {
      activePresets: presets.filter((p) => p.isActive).map(toRow),
      inactivePresets: presets.filter((p) => !p.isActive).map(toRow),
      roads,
      // La plantilla COMPLETA por id: el modal de edición necesita los rangos,
      // que la fila de la tabla ya no lleva.
      presetsById: Object.fromEntries(
        presets.map((preset) => [preset.id, preset])
      ),
    };
  } catch (error) {
    Sentry.captureException(error);
    console.error('Error loading route presets:', error);
    return vacio;
  }
};

export const RoutePresetsManagementLoader: (
  userData: AuthContextType | null
) => LoaderFunction = (userData) => async () => {
  // La promesa se devuelve PENDIENTE, sin esperarla: así el router termina la
  // navegación al instante y el `<Fallback>` enseña el Loader DENTRO de la
  // pantalla nueva. React Router 7 retiró `defer()` — ahora es este objeto.
  //
  // El `return` va explícito a propósito: el tripwire `todos-difieren` busca
  // `return { data:` para saber qué loaders vigilar, así que con la forma
  // implícita este fichero quedaría FUERA de la comprobación.
  return { data: load(userData) };
};

export default RoutePresetsManagementLoader;
